import { CommonModule } from "@angular/common";
import { NgModule } from "@angular/core";
import { RouterModule, Routes } from "@angular/router";
import { AdminToolsComponent } from "./components/admin-tools/admin-tools.component";
import { AnnotationCanvasComponent } from "./components/annotation-canvas/annotation-canvas.component";
import { EpicsConfirmDeactivateGuard } from "./components/epics/confirm-deactivate.guard";
import { EpicsComponent } from "./components/epics/epics.component";
import { FeatureCanvasConfirmDeactivateGuard } from "./components/feature-canvas/confirm-deactivate.guard";
import { FeatureCanvasComponent } from "./components/feature-canvas/feature-canvas.component";
import { ForgotPasswordComponent } from "./components/forgot-password/forgot-password.component";
import { HelpComponent } from "./components/help/help.component";
import { ImpressumComponent } from "./components/impressum/impressum.component";
import { LoginComponent } from "./components/login/login.component";
import { NewPasswordComponent } from "./components/new-password/new-password.component";
import { NotFound404Component } from "./components/not-found-404/not-found-404.component";
import { PlanningPokerHomeComponent } from "./components/planning-poker-home/planning-poker-home.component";
import { PrivacyNoticeComponent } from "./components/privacy-notice/privacy-notice.component";
import { ProjectDashboardComponent } from "./components/project-dashboard/project-dashboard.component";
import { ProjectOverviewAdminComponent } from "./components/project-overview-admin/project-overview-admin.component";
import { ProjectsComponent } from "./components/projects/projects.component";
import { QuestionnaireComponent } from "./components/questionnaire/questionnaire.component";
import { QuestionnairesComponent } from "./components/questionnaires/questionnaires.component";
import { RegistrationComponent } from "./components/registration/registration.component";
import { ResendActivationlinkComponent } from "./components/resend-activationlink/resend-activationlink.component";
import { RiskIndicatorComponent } from "./components/risk-indicator/risk-indicator.component";
import { RiskIndicatorsDeactivateGuard } from "./components/risk-indicators/risk-indicators-deactivate.guard";
import { RiskIndicatorsComponent } from "./components/risk-indicators/risk-indicators.component";
import { SprintsConfirmDeactivateGuard } from "./components/sprints/confirm-deactivate.guard";
import { SprintsComponent } from "./components/sprints/sprints.component";
import { SubscriptionComponent } from "./components/subscription/subscription.component";
import { TermsofserviceComponent } from "./components/termsofservice/termsofservice.component";
import { UserBehaviorComponent } from "./components/user-behavior/user-behavior.component";
import { UserDetailComponent } from "./components/user-detail/user-detail.component";
import { UserProfileComponent } from "./components/user-profile/user-profile.component";
import { AccessGuardService } from "./services/access-guard/access-guard.service";
import { LoggedInGuardService } from "./services/logged-in-guard/logged-in-guard.service";
import { QuestionnaireDeactivateGuard } from "./services/questionnaire-deactivate/questionnaire-deactivate.guard";

const routes: Routes = [
  { path: "404", component: NotFound404Component },

  { path: "", redirectTo: "login", pathMatch: "full" },

  {
    path: "questionnaires",
    data: {
      ShowNavBarQuestionnaire: true,
      ShowHeaderQuestionnaire: true,
      Breadcrumb: "questionnaire",
    },
    children: [
      {
        path: ":questionnaireId",
        data: { Breadcrumb: "questionnaire", NavItem: "questionnaire" },
        canDeactivate: [QuestionnaireDeactivateGuard],
        children: [
          {
            path: "",
            data: { Breadcrumb: "questionnaire" },
            component: QuestionnaireComponent,
          },
        ],
      },
    ],
  },

  {
    path: "projects",
    canActivate: [AccessGuardService],
    data: { ShowNavBar: true, ShowHeader: true, Breadcrumb: "projects" },
    children: [
      { path: "", component: ProjectsComponent, data: { NavItem: "projects" } },
      {
        path: ":id",
        data: { Breadcrumb: "project" },
        children: [
          {
            path: "",
            data: { NavItem: "dashboard" },
            component: ProjectDashboardComponent,
          },
          {
            path: "sprints",
            data: { Breadcrumb: "sprints", NavItem: "sprints" },
            children: [
              {
                path: "",
                component: SprintsComponent,
                canDeactivate: [SprintsConfirmDeactivateGuard],
              },
              {
                path: ":sprintId",
                data: { Breadcrumb: "sprint" },
                component: FeatureCanvasComponent,
                canDeactivate: [FeatureCanvasConfirmDeactivateGuard],
              },
            ],
          },
          {
            path: "questionnaires",
            data: { Breadcrumb: "questionnaires", NavItem: "questionnaire" },
            children: [
              { path: "", component: QuestionnairesComponent },
              {
                path: ":questionnaireId",
                data: {
                  Breadcrumb: "questionnaire",
                  NavItem: "questionnaire",
                  readonly: true,
                },
                children: [{ path: "", component: QuestionnaireComponent }],
              },
            ],
          },
          {
            path: "epics",
            data: { Breadcrumb: "epics", NavItem: "epics" },
            children: [
              {
                path: "",
                component: EpicsComponent,
                canDeactivate: [EpicsConfirmDeactivateGuard],
              },
            ],
          },
          {
            path: "annotations",
            data: {
              requiredRole: "annotations",
              Breadcrumb: "annotations",
              NavItem: "annotations",
            },
            children: [{ path: "", component: AnnotationCanvasComponent }],
          },
          {
            path: "retrospectives",
            data: { Breadcrumb: "retrospectives", NavItem: "retrospectives" },
            children: [
              {
                path: "",
                component: RiskIndicatorsComponent,
                canDeactivate: [RiskIndicatorsDeactivateGuard],
              },
              {
                path: ":riskIndicatorId",
                data: { Breadcrumb: "indicator" },
                component: RiskIndicatorComponent,
              },
            ],
          },
        ],
      },
    ],
  },

  { path: "planning-poker/:id", component: PlanningPokerHomeComponent },

  {
    path: "user-profile",
    component: UserProfileComponent,
    canActivate: [AccessGuardService],
    data: { Breadcrumb: "profile", ShowNavBar: true, ShowHeader: true },
  },

  {
    path: "subscription",
    component: SubscriptionComponent,
    canActivate: [AccessGuardService],
    data: { Breadcrumb: "subscription", ShowNavBar: true, ShowHeader: true },
  },

  {
    path: "subscription/:status",
    component: SubscriptionComponent,
    canActivate: [AccessGuardService],
    data: { Breadcrumb: "subscription", ShowNavBar: true, ShowHeader: true },
  },

  {
    path: "",
    canActivate: [LoggedInGuardService],
    children: [
      { path: "login", component: LoginComponent },
      { path: "registration", component: RegistrationComponent },
      { path: "registration/:token", component: RegistrationComponent },
      { path: "forgot-password", component: ForgotPasswordComponent },
      { path: "new-password", component: NewPasswordComponent },
      { path: "new-password/:token", component: NewPasswordComponent },
      { path: "resend-link", component: ResendActivationlinkComponent },
    ],
  },

  { path: "terms", component: TermsofserviceComponent },
  { path: "privacy", component: PrivacyNoticeComponent },
  { path: "impressum", component: ImpressumComponent },

  {
    path: "help",
    component: HelpComponent,
    data: {
      Breadcrumb: "help",
      NavItem: "help",
      ShowNavBar: true,
      ShowHeader: true,
    },
  },

  {
    path: "userBehavior",
    component: UserBehaviorComponent,
    data: {
      Breadcrumb: "userBehavior",
      NavItem: "userBehavior",
      ShowNavBar: true,
      ShowHeader: true,
    },
  },
  {
    path: "adminTools",
    component: AdminToolsComponent,
    data: {
      Breadcrumb: "adminTools",
      NavItem: "adminTools",
      ShowNavBar: true,
      ShowHeader: true,
    },
  },
  {
    path: "user-detail/:id",
    component: UserDetailComponent,
    data: {
      Breadcrumb: "user-detail",
      NavItem: "user-detail",
      ShowNavBar: true,
      ShowHeader: true,
    },
  },
  {
    path: "project-overview-admin",
    component: ProjectOverviewAdminComponent,
    data: {
      Breadcrumb: "project-overview-admin",
      NavItem: "project-overview-admin",
      ShowNavBar: true,
      ShowHeader: true,
    },
  },

  { path: "**", redirectTo: "/404" },
];

@NgModule({
  imports: [
    CommonModule,
    RouterModule.forRoot(routes, {
      paramsInheritanceStrategy: "always",
    }), // <-- installs Router routes, components and services
  ],
  declarations: [],
  exports: [RouterModule],
})
export class AppRoutingModule {}



// WEBPACK FOOTER //
// /agir/src/app/app.routing.ts